// ============================================================
// ASTRIQO CMS — Astro Events & Forecasts Data Layer
// Types, mock data, helpers for the entire CMS system
// ============================================================

// ────────────────────────────────────────────────────────────
// ENUMS & CONSTANTS
// ────────────────────────────────────────────────────────────

export type CmsStatus =
  | 'draft'
  | 'scheduled'
  | 'published'
  | 'archived'
  | 'hidden'
export type AstroEventType =
  | 'eclipse'
  | 'full_moon'
  | 'new_moon'
  | 'retrograde'
  | 'transit'
  | 'ingress'
  | 'other'
export type ForecastType = 'day' | 'week' | 'month'
export type ForecastScope = 'general' | 'zodiac'
export type ZodiacSign =
  | 'aries'
  | 'taurus'
  | 'gemini'
  | 'cancer'
  | 'leo'
  | 'virgo'
  | 'libra'
  | 'scorpio'
  | 'sagittarius'
  | 'capricorn'
  | 'aquarius'
  | 'pisces'
export type ImportanceLevel = 'low' | 'medium' | 'high' | 'critical'
export type SupportedLang = 'ru' | 'en'

export const ZODIAC_SIGNS: {
  key: ZodiacSign
  ru: string
  en: string
  symbol: string
  element: string
}[] = [
  { key: 'aries', ru: 'Овен', en: 'Aries', symbol: '♈', element: 'fire' },
  { key: 'taurus', ru: 'Телец', en: 'Taurus', symbol: '♉', element: 'earth' },
  { key: 'gemini', ru: 'Близнецы', en: 'Gemini', symbol: '♊', element: 'air' },
  { key: 'cancer', ru: 'Рак', en: 'Cancer', symbol: '♋', element: 'water' },
  { key: 'leo', ru: 'Лев', en: 'Leo', symbol: '♌', element: 'fire' },
  { key: 'virgo', ru: 'Дева', en: 'Virgo', symbol: '♍', element: 'earth' },
  { key: 'libra', ru: 'Весы', en: 'Libra', symbol: '♎', element: 'air' },
  {
    key: 'scorpio',
    ru: 'Скорпион',
    en: 'Scorpio',
    symbol: '♏',
    element: 'water',
  },
  {
    key: 'sagittarius',
    ru: 'Стрелец',
    en: 'Sagittarius',
    symbol: '♐',
    element: 'fire',
  },
  {
    key: 'capricorn',
    ru: 'Козерог',
    en: 'Capricorn',
    symbol: '♑',
    element: 'earth',
  },
  {
    key: 'aquarius',
    ru: 'Водолей',
    en: 'Aquarius',
    symbol: '♒',
    element: 'air',
  },
  { key: 'pisces', ru: 'Рыбы', en: 'Pisces', symbol: '♓', element: 'water' },
]

export const EVENT_TYPE_LABELS: Record<
  AstroEventType,
  { ru: string; en: string; color: string; bg: string }
> = {
  eclipse: {
    ru: 'Затмение',
    en: 'Eclipse',
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
  },
  full_moon: {
    ru: 'Полнолуние',
    en: 'Full Moon',
    color: 'text-indigo-700',
    bg: 'bg-indigo-50 border-indigo-200',
  },
  new_moon: {
    ru: 'Новолуние',
    en: 'New Moon',
    color: 'text-slate-700',
    bg: 'bg-slate-50 border-slate-200',
  },
  retrograde: {
    ru: 'Ретроград',
    en: 'Retrograde',
    color: 'text-rose-700',
    bg: 'bg-rose-50 border-rose-200',
  },
  transit: {
    ru: 'Транзит',
    en: 'Transit',
    color: 'text-purple-700',
    bg: 'bg-purple-50 border-purple-200',
  },
  ingress: {
    ru: 'Ингрессия',
    en: 'Ingress',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50 border-emerald-200',
  },
  other: {
    ru: 'Другое',
    en: 'Other',
    color: 'text-slate-600',
    bg: 'bg-slate-50 border-slate-200',
  },
}

export const STATUS_LABELS: Record<
  CmsStatus,
  { ru: string; en: string; variant: string }
> = {
  draft: { ru: 'Черновик', en: 'Draft', variant: 'neutral' },
  scheduled: { ru: 'Запланировано', en: 'Scheduled', variant: 'warning' },
  published: { ru: 'Опубликовано', en: 'Published', variant: 'success' },
  archived: { ru: 'Архив', en: 'Archived', variant: 'neutral' },
  hidden: { ru: 'Скрыто', en: 'Hidden', variant: 'error' },
}

export const FORECAST_TYPE_LABELS: Record<
  ForecastType,
  { ru: string; en: string }
> = {
  day: { ru: 'Дневной', en: 'Daily' },
  week: { ru: 'Недельный', en: 'Weekly' },
  month: { ru: 'Месячный', en: 'Monthly' },
}

export const IMPORTANCE_LABELS: Record<
  ImportanceLevel,
  { ru: string; color: string }
> = {
  low: { ru: 'Низкая', color: 'text-slate-500' },
  medium: { ru: 'Средняя', color: 'text-blue-600' },
  high: { ru: 'Высокая', color: 'text-amber-600' },
  critical: { ru: 'Критическая', color: 'text-rose-600' },
}

// ────────────────────────────────────────────────────────────
// INTERFACES
// ────────────────────────────────────────────────────────────

export interface AstroEvent {
  id: string
  title: string
  slug: string
  event_type: AstroEventType
  date_start: string // ISO date
  date_end: string | null
  time_exact: string | null // HH:mm
  status: CmsStatus
  lang: SupportedLang
  // Content
  description_short: string
  description_full: string
  influence: string
  recommendations: string
  importance: ImportanceLevel
  tags: string[]
  // System
  created_at: string
  updated_at: string
  created_by: string
  publish_at: string | null
  hide_at: string | null
  manual_publish: boolean
  views: number
}

export interface ForecastContent {
  general: string
  love_percent: number
  love_text: string
  career_percent: number
  career_text: string
  health_percent: number
  health_text: string
  mood: string
  lucky_color: string
  lucky_number: number
  recommendation: string
  cta_text: string | null
  cta_link: string | null
}

export interface Forecast {
  id: string
  title: string
  slug: string
  forecast_type: ForecastType
  scope: ForecastScope
  zodiac_sign: ZodiacSign | null // null if scope=general
  lang: SupportedLang
  date_start: string
  date_end: string
  status: CmsStatus
  content: ForecastContent
  // System
  created_at: string
  updated_at: string
  created_by: string
  publish_at: string | null
  hide_at: string | null
  ai_generated: boolean
  source_template: string | null
  views: number
}

export interface ForecastTemplate {
  id: string
  title: string
  forecast_type: ForecastType
  lang: SupportedLang
  required_fields: string[]
  content_blocks: string[]
  generation_rules: string
  created_at: string
  updated_at: string
}

export interface ChangeLogEntry {
  id: string
  entity_type: 'event' | 'forecast' | 'template'
  entity_id: string
  entity_title: string
  action:
    | 'created'
    | 'updated'
    | 'published'
    | 'archived'
    | 'hidden'
    | 'deleted'
    | 'status_changed'
    | 'ai_generated'
  old_value: string | null
  new_value: string | null
  user_name: string
  timestamp: string
}

export interface AIGenerationLog {
  id: string
  generation_type: 'single' | 'bulk'
  forecast_type: ForecastType
  signs: ZodiacSign[]
  period_start: string
  period_end: string
  lang: SupportedLang
  template_id: string | null
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  total_items: number
  completed_items: number
  failed_items: number
  created_at: string
  created_by: string
  error_message: string | null
}

// ────────────────────────────────────────────────────────────
// MOCK DATA — ASTRO EVENTS
// ────────────────────────────────────────────────────────────

export const initialAstroEvents: AstroEvent[] = [
  {
    id: 'evt-001',
    title: 'Солнечное затмение в Овне',
    slug: 'solar-eclipse-aries-2026-03',
    event_type: 'eclipse',
    date_start: '2026-03-29',
    date_end: '2026-03-29',
    time_exact: '14:32',
    status: 'published',
    lang: 'ru',
    description_short:
      'Мощное солнечное затмение в знаке Овна открывает новый цикл инициатив.',
    description_full:
      'Солнечное затмение в Овне — это мощный астрологический импульс, который запускает новые циклы в жизни каждого. Овен как первый знак зодиака символизирует начало, и это затмение буквально открывает двери для смелых решений, новых проектов и кардинальных перемен. Энергия затмения будет ощущаться за неделю до и после точной даты.',
    influence:
      'Сильное влияние на личную инициативу, карьерные решения и отношения. Особенно затронет кардинальные знаки: Овен, Рак, Весы, Козерог.',
    recommendations:
      'Не принимайте импульсивных решений в день затмения. Используйте энергию для планирования, а не для действий. Медитация и самоанализ будут особенно полезны.',
    importance: 'critical',
    tags: ['затмение', 'овен', 'новый цикл', 'кардинальные знаки'],
    created_at: '2026-02-15T10:00:00Z',
    updated_at: '2026-03-10T14:30:00Z',
    created_by: 'Администратор',
    publish_at: '2026-03-15T00:00:00Z',
    hide_at: '2026-04-15T00:00:00Z',
    manual_publish: false,
    views: 12450,
  },
  {
    id: 'evt-002',
    title: 'Полнолуние в Весах',
    slug: 'full-moon-libra-2026-03',
    event_type: 'full_moon',
    date_start: '2026-03-13',
    date_end: '2026-03-13',
    time_exact: '09:55',
    status: 'published',
    lang: 'ru',
    description_short:
      'Полнолуние в Весах подсвечивает тему баланса в отношениях.',
    description_full:
      'Полнолуние в Весах приносит кульминацию в сфере партнёрства и отношений. Это время, когда скрытые напряжения выходят на поверхность, а решения о будущем отношений становятся неизбежными. Весы управляют гармонией, справедливостью и красотой — обратите внимание на эти сферы.',
    influence:
      'Отношения, партнёрство, юридические вопросы, эстетика. Воздушные знаки почувствуют влияние сильнее всего.',
    recommendations:
      'Ищите компромиссы, избегайте конфликтов. Хорошее время для завершения переговоров и подписания договоров.',
    importance: 'high',
    tags: ['полнолуние', 'весы', 'отношения', 'баланс'],
    created_at: '2026-02-20T08:00:00Z',
    updated_at: '2026-03-05T11:00:00Z',
    created_by: 'Администратор',
    publish_at: '2026-03-06T00:00:00Z',
    hide_at: '2026-03-20T00:00:00Z',
    manual_publish: false,
    views: 8920,
  },
  {
    id: 'evt-003',
    title: 'Ретроградный Меркурий в Овне',
    slug: 'mercury-retrograde-aries-2026',
    event_type: 'retrograde',
    date_start: '2026-03-15',
    date_end: '2026-04-07',
    time_exact: null,
    status: 'published',
    lang: 'ru',
    description_short:
      'Меркурий замедляется в Овне — время пересмотра планов и коммуникаций.',
    description_full:
      'Ретроградный Меркурий в Овне — период, когда стоит замедлиться и пересмотреть свои планы. Коммуникации могут быть затруднены, техника может давать сбои, а старые знакомые могут напомнить о себе. Это не время для начала новых проектов, но отличный период для доработки существующих.',
    influence:
      'Коммуникации, технологии, транспорт, документы. Все знаки почувствуют влияние, но Овен, Дева и Близнецы — особенно.',
    recommendations:
      'Перепроверяйте всё дважды. Делайте резервные копии данных. Не подписывайте важные контракты. Завершайте начатое.',
    importance: 'high',
    tags: ['ретроград', 'меркурий', 'овен', 'коммуникации'],
    created_at: '2026-02-01T09:00:00Z',
    updated_at: '2026-03-10T16:00:00Z',
    created_by: 'Администратор',
    publish_at: '2026-03-08T00:00:00Z',
    hide_at: '2026-04-14T00:00:00Z',
    manual_publish: false,
    views: 15600,
  },
  {
    id: 'evt-004',
    title: 'Новолуние в Рыбах',
    slug: 'new-moon-pisces-2026-02',
    event_type: 'new_moon',
    date_start: '2026-02-28',
    date_end: '2026-02-28',
    time_exact: '03:45',
    status: 'archived',
    lang: 'ru',
    description_short:
      'Новолуние в Рыбах — время для мечтаний и духовных практик.',
    description_full:
      'Новолуние в Рыбах открывает портал для интуиции, творчества и духовного роста. Это самое мистическое новолуние года, когда границы между реальностью и воображением размываются.',
    influence:
      'Духовность, творчество, интуиция, сны. Водные знаки получат максимум энергии.',
    recommendations:
      'Медитируйте, ведите дневник снов, занимайтесь творчеством. Избегайте алкоголя и веществ, изменяющих сознание.',
    importance: 'medium',
    tags: ['новолуние', 'рыбы', 'духовность', 'интуиция'],
    created_at: '2026-02-10T07:00:00Z',
    updated_at: '2026-03-01T00:00:00Z',
    created_by: 'Администратор',
    publish_at: '2026-02-21T00:00:00Z',
    hide_at: '2026-03-07T00:00:00Z',
    manual_publish: false,
    views: 6340,
  },
  {
    id: 'evt-005',
    title: 'Юпитер входит в Рак',
    slug: 'jupiter-enters-cancer-2026',
    event_type: 'ingress',
    date_start: '2026-06-09',
    date_end: '2027-06-30',
    time_exact: '18:20',
    status: 'scheduled',
    lang: 'ru',
    description_short:
      'Юпитер переходит в знак Рака — начало периода роста через заботу и семью.',
    description_full:
      'Юпитер в Раке — одно из самых благоприятных положений планеты-благодетеля. Этот транзит длится около года и приносит расширение в сферах семьи, дома, эмоциональной безопасности и заботы. Рак — знак экзальтации Юпитера, что усиливает его позитивное влияние.',
    influence:
      'Семья, дом, недвижимость, эмоциональное благополучие. Водные знаки получат максимум благ.',
    recommendations:
      'Инвестируйте в семью и дом. Расширяйте зону комфорта. Работайте с эмоциональными паттернами.',
    importance: 'critical',
    tags: ['юпитер', 'рак', 'экзальтация', 'семья', 'долгосрочный'],
    created_at: '2026-03-01T10:00:00Z',
    updated_at: '2026-03-15T12:00:00Z',
    created_by: 'Администратор',
    publish_at: '2026-05-25T00:00:00Z',
    hide_at: '2027-07-15T00:00:00Z',
    manual_publish: false,
    views: 0,
  },
  {
    id: 'evt-006',
    title: 'Венера в Тельце — транзит',
    slug: 'venus-taurus-transit-2026-04',
    event_type: 'transit',
    date_start: '2026-04-14',
    date_end: '2026-05-09',
    time_exact: null,
    status: 'draft',
    lang: 'ru',
    description_short:
      'Венера возвращается в свой домицил — время для любви, красоты и финансов.',
    description_full:
      'Венера в Тельце чувствует себя как дома. Это период, когда чувственные удовольствия, финансовая стабильность и эстетика выходят на первый план.',
    influence:
      'Любовь, финансы, красота, комфорт. Земные знаки получат особую поддержку.',
    recommendations:
      'Балуйте себя, но в меру. Хорошее время для инвестиций и покупок предметов искусства.',
    importance: 'medium',
    tags: ['венера', 'телец', 'домицил', 'любовь', 'финансы'],
    created_at: '2026-03-18T14:00:00Z',
    updated_at: '2026-03-18T14:00:00Z',
    created_by: 'Администратор',
    publish_at: null,
    hide_at: null,
    manual_publish: true,
    views: 0,
  },
]

// ────────────────────────────────────────────────────────────
// MOCK DATA — FORECASTS
// ────────────────────────────────────────────────────────────

const createDailyForecast = (
  id: string,
  sign: ZodiacSign | null,
  date: string,
  status: CmsStatus,
  content: Partial<ForecastContent>,
  ai: boolean = false,
): Forecast => {
  const signInfo = sign ? ZODIAC_SIGNS.find((z) => z.key === sign) : null
  const title = sign
    ? `Прогноз на ${date} — ${signInfo?.ru}`
    : `Общий прогноз на ${date}`
  return {
    id,
    title,
    slug: `forecast-${date}-${sign || 'general'}`,
    forecast_type: 'day',
    scope: sign ? 'zodiac' : 'general',
    zodiac_sign: sign,
    lang: 'ru',
    date_start: date,
    date_end: date,
    status,
    content: {
      general:
        content.general ||
        'День принесёт новые возможности для роста и развития.',
      love_percent: content.love_percent ?? 75,
      love_text:
        content.love_text ||
        'Романтическая энергия на подъёме. Будьте открыты к новым знакомствам.',
      career_percent: content.career_percent ?? 80,
      career_text:
        content.career_text ||
        'Деловые переговоры пройдут успешно. Не упускайте шанс проявить инициативу.',
      health_percent: content.health_percent ?? 70,
      health_text:
        content.health_text ||
        'Уделите внимание физической активности. Прогулки на свежем воздухе будут особенно полезны.',
      mood: content.mood || 'Оптимистичное',
      lucky_color: content.lucky_color || 'Синий',
      lucky_number: content.lucky_number ?? 7,
      recommendation:
        content.recommendation ||
        'Доверяйте своей интуиции и не бойтесь перемен.',
      cta_text: content.cta_text || null,
      cta_link: content.cta_link || null,
    },
    created_at: '2026-03-18T08:00:00Z',
    updated_at: '2026-03-18T08:00:00Z',
    created_by: ai ? 'AI Generator' : 'Администратор',
    publish_at: `${date}T00:00:00Z`,
    hide_at: `${date}T23:59:59Z`,
    ai_generated: ai,
    source_template: ai ? 'tpl-001' : null,
    views: status === 'published' ? Math.floor(Math.random() * 3000) + 500 : 0,
  }
}

export const initialForecasts: Forecast[] = [
  // Today's forecasts (published)
  createDailyForecast('frc-001', 'aries', '2026-03-20', 'published', {
    general:
      'Овны сегодня полны энергии и решимости. Солнце в вашем знаке даёт мощный заряд уверенности. Используйте этот день для начала новых проектов.',
    love_percent: 85,
    love_text:
      'Страсть и романтика витают в воздухе. Одинокие Овны могут встретить интересного человека.',
    career_percent: 90,
    career_text:
      'Отличный день для карьерных инициатив. Ваша энергия заразительна — коллеги поддержат ваши идеи.',
    health_percent: 75,
    health_text:
      'Много энергии, но не перегружайтесь. Баланс между активностью и отдыхом — ключ к хорошему самочувствию.',
    mood: 'Энергичное и решительное',
    lucky_color: 'Красный',
    lucky_number: 1,
    recommendation: 'Действуйте смело, но обдуманно. Сегодня ваш день!',
  }),
  createDailyForecast('frc-002', 'taurus', '2026-03-20', 'published', {
    general:
      'Тельцы сегодня сосредоточены на финансовых вопросах. Венера поддерживает ваши материальные начинания.',
    love_percent: 70,
    love_text: 'Стабильность в отношениях. Порадуйте партнёра вкусным ужином.',
    career_percent: 85,
    career_text:
      'Финансовые решения будут удачными. Доверяйте своему чутью в денежных вопросах.',
    health_percent: 80,
    health_text:
      'Хорошее самочувствие. Побалуйте себя массажем или спа-процедурой.',
    mood: 'Спокойное и уверенное',
    lucky_color: 'Зелёный',
    lucky_number: 6,
  }),
  createDailyForecast('frc-003', 'gemini', '2026-03-20', 'published', {
    general:
      'Близнецы сегодня блистают в общении. Меркурий усиливает вашу коммуникабельность и остроумие.',
    love_percent: 80,
    love_text:
      'Флирт и лёгкость в общении привлекут внимание. Не бойтесь быть собой.',
    career_percent: 75,
    career_text:
      'Переговоры и презентации пройдут блестяще. Ваше красноречие — ваш главный козырь.',
    mood: 'Общительное и весёлое',
    lucky_color: 'Жёлтый',
    lucky_number: 5,
  }),
  createDailyForecast('frc-004', null, '2026-03-20', 'published', {
    general:
      'Сегодня Солнце входит в знак Овна — начинается астрологический новый год! Это мощный день для новых начинаний, постановки целей и смелых решений. Энергия обновления доступна всем знакам зодиака.',
    love_percent: 78,
    love_text:
      'Новая энергия в отношениях. Хороший день для откровенных разговоров.',
    career_percent: 82,
    career_text:
      'Начинайте новые проекты. Энергия Овна поддерживает инициативу.',
    health_percent: 85,
    health_text:
      'Прилив сил и энергии. Используйте его для физической активности.',
    mood: 'Воодушевлённое',
    lucky_color: 'Золотой',
    lucky_number: 9,
    recommendation:
      'Астрологический новый год — идеальное время задать намерения на весь следующий цикл.',
  }),
  // Tomorrow's forecasts (scheduled)
  createDailyForecast(
    'frc-005',
    'aries',
    '2026-03-21',
    'scheduled',
    {
      general:
        'Второй день Солнца в вашем знаке. Энергия нарастает, но будьте осторожны с импульсивностью.',
      love_percent: 72,
      career_percent: 88,
      mood: 'Активное',
      lucky_color: 'Оранжевый',
      lucky_number: 3,
    },
    true,
  ),
  createDailyForecast(
    'frc-006',
    'taurus',
    '2026-03-21',
    'scheduled',
    {
      general:
        'Спокойный день для размышлений. Луна поддерживает ваши творческие начинания.',
      love_percent: 78,
      career_percent: 70,
      mood: 'Задумчивое',
      lucky_color: 'Бирюзовый',
      lucky_number: 4,
    },
    true,
  ),
  // Weekly forecast
  {
    id: 'frc-w01',
    title: 'Недельный прогноз — Овен (17–23 марта)',
    slug: 'weekly-aries-2026-03-17',
    forecast_type: 'week',
    scope: 'zodiac',
    zodiac_sign: 'aries',
    lang: 'ru',
    date_start: '2026-03-17',
    date_end: '2026-03-23',
    status: 'published',
    content: {
      general:
        'Неделя начинается с мощного энергетического подъёма. Солнце входит в ваш знак 20 марта, что даёт старт вашему личному новому году. Используйте эту неделю для масштабного планирования и запуска проектов, которые откладывали.',
      love_percent: 80,
      love_text:
        'Романтическая неделя. Середина недели особенно благоприятна для свиданий и признаний.',
      career_percent: 88,
      career_text:
        'Карьерный рост на горизонте. Четверг и пятница — лучшие дни для важных встреч.',
      health_percent: 75,
      health_text:
        'Следите за режимом сна. Энергии много, но восстановление тоже важно.',
      mood: 'Восходящее',
      lucky_color: 'Алый',
      lucky_number: 11,
      recommendation:
        'Ставьте большие цели — эта неделя создана для амбициозных планов.',
      cta_text: 'Получить персональный прогноз',
      cta_link: '/services',
    },
    created_at: '2026-03-14T10:00:00Z',
    updated_at: '2026-03-16T15:00:00Z',
    created_by: 'Администратор',
    publish_at: '2026-03-17T00:00:00Z',
    hide_at: '2026-03-24T00:00:00Z',
    ai_generated: false,
    source_template: null,
    views: 4560,
  },
  // Monthly forecast
  {
    id: 'frc-m01',
    title: 'Месячный прогноз — Скорпион (Март 2026)',
    slug: 'monthly-scorpio-2026-03',
    forecast_type: 'month',
    scope: 'zodiac',
    zodiac_sign: 'scorpio',
    lang: 'ru',
    date_start: '2026-03-01',
    date_end: '2026-03-31',
    status: 'published',
    content: {
      general:
        'Март 2026 для Скорпионов — месяц глубоких трансформаций. Полнолуние 13 марта в Весах подсветит сферу партнёрства, а ретроградный Меркурий с 15 марта заставит пересмотреть рабочие процессы. Конец месяца принесёт солнечное затмение, которое откроет новые возможности.',
      love_percent: 82,
      love_text:
        'Интенсивный месяц для отношений. Первая половина — выяснение отношений, вторая — новый уровень близости.',
      career_percent: 76,
      career_text:
        'Карьерные перемены возможны. Не торопитесь с решениями до конца ретрограда Меркурия.',
      health_percent: 68,
      health_text:
        'Эмоциональное напряжение может сказаться на здоровье. Практикуйте релаксацию.',
      mood: 'Трансформационное',
      lucky_color: 'Бордовый',
      lucky_number: 8,
      recommendation:
        'Доверяйте процессу трансформации. То, что уходит — уходит не зря.',
      cta_text: 'Заказать полный анализ месяца',
      cta_link: '/services',
    },
    created_at: '2026-02-25T09:00:00Z',
    updated_at: '2026-02-28T14:00:00Z',
    created_by: 'Администратор',
    publish_at: '2026-03-01T00:00:00Z',
    hide_at: '2026-04-01T00:00:00Z',
    ai_generated: false,
    source_template: null,
    views: 7890,
  },
  // Draft forecasts
  createDailyForecast('frc-d01', 'cancer', '2026-03-22', 'draft', {
    general: 'Черновик прогноза для Рака...',
    love_percent: 65,
    career_percent: 70,
  }),
  createDailyForecast('frc-d02', 'leo', '2026-03-22', 'draft', {
    general: 'Черновик прогноза для Льва...',
    love_percent: 80,
    career_percent: 85,
  }),
]

// ────────────────────────────────────────────────────────────
// MOCK DATA — TEMPLATES
// ────────────────────────────────────────────────────────────

export const initialForecastTemplates: ForecastTemplate[] = [
  {
    id: 'tpl-001',
    title: 'Стандартный дневной прогноз',
    forecast_type: 'day',
    lang: 'ru',
    required_fields: [
      'general',
      'love_text',
      'career_text',
      'health_text',
      'mood',
      'lucky_color',
      'lucky_number',
    ],
    content_blocks: [
      'Общий прогноз',
      'Любовь',
      'Карьера',
      'Здоровье',
      'Настроение',
      'Цвет и число дня',
    ],
    generation_rules:
      'Тон: дружелюбный, но профессиональный. Длина общего прогноза: 2-3 предложения. Каждый раздел: 1-2 предложения. Учитывать текущие транзиты и аспекты.',
    created_at: '2026-01-15T10:00:00Z',
    updated_at: '2026-03-01T12:00:00Z',
  },
  {
    id: 'tpl-002',
    title: 'Расширенный недельный прогноз',
    forecast_type: 'week',
    lang: 'ru',
    required_fields: [
      'general',
      'love_text',
      'career_text',
      'health_text',
      'mood',
      'recommendation',
    ],
    content_blocks: [
      'Обзор недели',
      'Любовь и отношения',
      'Карьера и финансы',
      'Здоровье и энергия',
      'Ключевые дни',
      'Рекомендация недели',
    ],
    generation_rules:
      'Тон: экспертный, вдохновляющий. Длина общего прогноза: 4-5 предложений. Упоминать конкретные дни недели. Связывать с текущими астрологическими событиями.',
    created_at: '2026-01-20T10:00:00Z',
    updated_at: '2026-02-15T09:00:00Z',
  },
  {
    id: 'tpl-003',
    title: 'Месячный обзор',
    forecast_type: 'month',
    lang: 'ru',
    required_fields: [
      'general',
      'love_text',
      'career_text',
      'health_text',
      'mood',
      'recommendation',
      'cta_text',
    ],
    content_blocks: [
      'Обзор месяца',
      'Ключевые даты',
      'Любовь',
      'Карьера',
      'Здоровье',
      'Итог и рекомендация',
      'CTA',
    ],
    generation_rules:
      'Тон: глубокий, аналитический. Длина общего прогноза: 5-7 предложений. Обязательно упоминать ключевые астрологические события месяца. Включать CTA на услуги.',
    created_at: '2026-02-01T10:00:00Z',
    updated_at: '2026-02-20T11:00:00Z',
  },
]

// ────────────────────────────────────────────────────────────
// MOCK DATA — CHANGE LOGS
// ────────────────────────────────────────────────────────────

export const initialChangeLogs: ChangeLogEntry[] = [
  {
    id: 'log-001',
    entity_type: 'event',
    entity_id: 'evt-001',
    entity_title: 'Солнечное затмение в Овне',
    action: 'created',
    old_value: null,
    new_value: 'draft',
    user_name: 'Администратор',
    timestamp: '2026-02-15T10:00:00Z',
  },
  {
    id: 'log-002',
    entity_type: 'event',
    entity_id: 'evt-001',
    entity_title: 'Солнечное затмение в Овне',
    action: 'status_changed',
    old_value: 'draft',
    new_value: 'scheduled',
    user_name: 'Администратор',
    timestamp: '2026-02-15T10:30:00Z',
  },
  {
    id: 'log-003',
    entity_type: 'event',
    entity_id: 'evt-001',
    entity_title: 'Солнечное затмение в Овне',
    action: 'published',
    old_value: 'scheduled',
    new_value: 'published',
    user_name: 'Система',
    timestamp: '2026-03-15T00:00:00Z',
  },
  {
    id: 'log-004',
    entity_type: 'forecast',
    entity_id: 'frc-001',
    entity_title: 'Прогноз на 2026-03-20 — Овен',
    action: 'created',
    old_value: null,
    new_value: 'draft',
    user_name: 'Администратор',
    timestamp: '2026-03-18T08:00:00Z',
  },
  {
    id: 'log-005',
    entity_type: 'forecast',
    entity_id: 'frc-001',
    entity_title: 'Прогноз на 2026-03-20 — Овен',
    action: 'published',
    old_value: 'draft',
    new_value: 'published',
    user_name: 'Администратор',
    timestamp: '2026-03-18T09:00:00Z',
  },
  {
    id: 'log-006',
    entity_type: 'forecast',
    entity_id: 'frc-005',
    entity_title: 'Прогноз на 2026-03-21 — Овен',
    action: 'ai_generated',
    old_value: null,
    new_value: 'draft',
    user_name: 'AI Generator',
    timestamp: '2026-03-19T02:00:00Z',
  },
  {
    id: 'log-007',
    entity_type: 'template',
    entity_id: 'tpl-001',
    entity_title: 'Стандартный дневной прогноз',
    action: 'updated',
    old_value: null,
    new_value: null,
    user_name: 'Администратор',
    timestamp: '2026-03-01T12:00:00Z',
  },
  {
    id: 'log-008',
    entity_type: 'event',
    entity_id: 'evt-002',
    entity_title: 'Полнолуние в Весах',
    action: 'published',
    old_value: 'scheduled',
    new_value: 'published',
    user_name: 'Система',
    timestamp: '2026-03-06T00:00:00Z',
  },
  {
    id: 'log-009',
    entity_type: 'forecast',
    entity_id: 'frc-w01',
    entity_title: 'Недельный прогноз — Овен',
    action: 'created',
    old_value: null,
    new_value: 'draft',
    user_name: 'Администратор',
    timestamp: '2026-03-14T10:00:00Z',
  },
  {
    id: 'log-010',
    entity_type: 'forecast',
    entity_id: 'frc-w01',
    entity_title: 'Недельный прогноз — Овен',
    action: 'published',
    old_value: 'scheduled',
    new_value: 'published',
    user_name: 'Администратор',
    timestamp: '2026-03-17T00:00:00Z',
  },
]

// ────────────────────────────────────────────────────────────
// MOCK DATA — AI GENERATION LOGS
// ────────────────────────────────────────────────────────────

export const initialAIGenerationLogs: AIGenerationLog[] = [
  {
    id: 'aig-001',
    generation_type: 'bulk',
    forecast_type: 'day',
    signs: [
      'aries',
      'taurus',
      'gemini',
      'cancer',
      'leo',
      'virgo',
      'libra',
      'scorpio',
      'sagittarius',
      'capricorn',
      'aquarius',
      'pisces',
    ],
    period_start: '2026-03-21',
    period_end: '2026-03-21',
    lang: 'ru',
    template_id: 'tpl-001',
    status: 'completed',
    total_items: 12,
    completed_items: 12,
    failed_items: 0,
    created_at: '2026-03-19T02:00:00Z',
    created_by: 'Администратор',
    error_message: null,
  },
  {
    id: 'aig-002',
    generation_type: 'bulk',
    forecast_type: 'day',
    signs: [
      'aries',
      'taurus',
      'gemini',
      'cancer',
      'leo',
      'virgo',
      'libra',
      'scorpio',
      'sagittarius',
      'capricorn',
      'aquarius',
      'pisces',
    ],
    period_start: '2026-03-22',
    period_end: '2026-03-28',
    lang: 'ru',
    template_id: 'tpl-001',
    status: 'in_progress',
    total_items: 84,
    completed_items: 36,
    failed_items: 2,
    created_at: '2026-03-20T01:00:00Z',
    created_by: 'Администратор',
    error_message: null,
  },
]

// ────────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ────────────────────────────────────────────────────────────

export function getZodiacLabel(
  sign: ZodiacSign,
  lang: SupportedLang = 'ru',
): string {
  const z = ZODIAC_SIGNS.find((s) => s.key === sign)
  return z ? (lang === 'ru' ? z.ru : z.en) : sign
}

export function getZodiacSymbol(sign: ZodiacSign): string {
  return ZODIAC_SIGNS.find((s) => s.key === sign)?.symbol || ''
}

export function getEventTypeLabel(
  type: AstroEventType,
  lang: SupportedLang = 'ru',
): string {
  return lang === 'ru' ? EVENT_TYPE_LABELS[type].ru : EVENT_TYPE_LABELS[type].en
}

export function getStatusLabel(
  status: CmsStatus,
  lang: SupportedLang = 'ru',
): string {
  return lang === 'ru' ? STATUS_LABELS[status].ru : STATUS_LABELS[status].en
}

export function getStatusVariant(status: CmsStatus): string {
  return STATUS_LABELS[status].variant
}

export function getForecastTypeLabel(
  type: ForecastType,
  lang: SupportedLang = 'ru',
): string {
  return lang === 'ru'
    ? FORECAST_TYPE_LABELS[type].ru
    : FORECAST_TYPE_LABELS[type].en
}

/** Simulate auto-publish: check if item should change status based on current date */
export function computeAutoStatus(
  currentStatus: CmsStatus,
  publishAt: string | null,
  hideAt: string | null,
  manualPublish: boolean = false,
  now: Date = new Date(),
): CmsStatus {
  if (currentStatus === 'hidden') return 'hidden'
  if (currentStatus === 'draft' && manualPublish) return 'draft'

  const nowMs = now.getTime()

  if (hideAt && nowMs > new Date(hideAt).getTime()) return 'archived'
  if (publishAt && nowMs >= new Date(publishAt).getTime()) return 'published'
  if (publishAt && nowMs < new Date(publishAt).getTime()) return 'scheduled'

  return currentStatus
}

/** Generate a slug from title */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[а-яё]/g, (char) => {
      const map: Record<string, string> = {
        а: 'a',
        б: 'b',
        в: 'v',
        г: 'g',
        д: 'd',
        е: 'e',
        ё: 'yo',
        ж: 'zh',
        з: 'z',
        и: 'i',
        й: 'y',
        к: 'k',
        л: 'l',
        м: 'm',
        н: 'n',
        о: 'o',
        п: 'p',
        р: 'r',
        с: 's',
        т: 't',
        у: 'u',
        ф: 'f',
        х: 'kh',
        ц: 'ts',
        ч: 'ch',
        ш: 'sh',
        щ: 'shch',
        ъ: '',
        ы: 'y',
        ь: '',
        э: 'e',
        ю: 'yu',
        я: 'ya',
      }
      return map[char] || char
    })
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/** Format date for display */
export function formatDateRu(dateStr: string): string {
  const months = [
    'Янв',
    'Фев',
    'Мар',
    'Апр',
    'Май',
    'Июн',
    'Июл',
    'Авг',
    'Сен',
    'Окт',
    'Ноя',
    'Дек',
  ]
  const d = new Date(dateStr)
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
}

export function formatDateTimeFull(dateStr: string): string {
  const d = new Date(dateStr)
  const months = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ]
  const hours = d.getHours().toString().padStart(2, '0')
  const mins = d.getMinutes().toString().padStart(2, '0')
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${hours}:${mins}`
}

/** Get forecast coverage stats */
export function getForecastCoverageStats(forecasts: Forecast[]) {
  const today = new Date().toISOString().split('T')[0]
  const published = forecasts.filter((f) => f.status === 'published')
  const scheduled = forecasts.filter((f) => f.status === 'scheduled')
  const drafts = forecasts.filter((f) => f.status === 'draft')
  const archived = forecasts.filter((f) => f.status === 'archived')
  const daily = forecasts.filter((f) => f.forecast_type === 'day')
  const weekly = forecasts.filter((f) => f.forecast_type === 'week')
  const monthly = forecasts.filter((f) => f.forecast_type === 'month')
  const aiGenerated = forecasts.filter((f) => f.ai_generated)
  const bySign: Record<string, number> = {}
  ZODIAC_SIGNS.forEach((z) => {
    bySign[z.key] = forecasts.filter((f) => f.zodiac_sign === z.key).length
  })
  return {
    published: published.length,
    scheduled: scheduled.length,
    drafts: drafts.length,
    archived: archived.length,
    daily: daily.length,
    weekly: weekly.length,
    monthly: monthly.length,
    aiGenerated: aiGenerated.length,
    bySign,
    total: forecasts.length,
  }
}

/** Create empty forecast */
export function createEmptyForecast(
  type: ForecastType,
  sign: ZodiacSign | null,
  date: string,
  lang: SupportedLang = 'ru',
): Forecast {
  const signInfo = sign ? ZODIAC_SIGNS.find((z) => z.key === sign) : null
  const title = sign
    ? `${FORECAST_TYPE_LABELS[type].ru} прогноз — ${signInfo?.ru} (${formatDateRu(date)})`
    : `Общий ${FORECAST_TYPE_LABELS[type].ru.toLowerCase()} прогноз (${formatDateRu(date)})`
  const endDate =
    type === 'day'
      ? date
      : type === 'week'
        ? new Date(new Date(date).getTime() + 6 * 86400000)
            .toISOString()
            .split('T')[0]
        : new Date(
            new Date(date).getFullYear(),
            new Date(date).getMonth() + 1,
            0,
          )
            .toISOString()
            .split('T')[0]
  return {
    id: `frc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    title,
    slug: generateSlug(title),
    forecast_type: type,
    scope: sign ? 'zodiac' : 'general',
    zodiac_sign: sign,
    lang,
    date_start: date,
    date_end: endDate,
    status: 'draft',
    content: {
      general: '',
      love_percent: 50,
      love_text: '',
      career_percent: 50,
      career_text: '',
      health_percent: 50,
      health_text: '',
      mood: '',
      lucky_color: '',
      lucky_number: 0,
      recommendation: '',
      cta_text: null,
      cta_link: null,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'Администратор',
    publish_at: null,
    hide_at: null,
    ai_generated: false,
    source_template: null,
    views: 0,
  }
}

/** Create empty astro event */
export function createEmptyEvent(lang: SupportedLang = 'ru'): AstroEvent {
  return {
    id: `evt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    title: '',
    slug: '',
    event_type: 'other',
    date_start: new Date().toISOString().split('T')[0],
    date_end: null,
    time_exact: null,
    status: 'draft',
    lang,
    description_short: '',
    description_full: '',
    influence: '',
    recommendations: '',
    importance: 'medium',
    tags: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'Администратор',
    publish_at: null,
    hide_at: null,
    manual_publish: false,
    views: 0,
  }
}
